<template>
    <v-simple-table class="results" v-if="standards.length">
        <template v-slot:default>
            <thead>
                <tr is="sortable-table-header" :headers="headers"></tr>
            </thead>
            <tbody>
                <template v-for="(standard, index) in sortedStandards">
                    <tr
                        :id="standard.hex"
                        :key="standard.hex"
                        :class="['summary', { expanded: standard.isExpanded }, { 'area-of-need': 'Area of Need' === standard.status }]"
                        @click="expandable && (standard.isExpanded = !standard.isExpanded)"
                        v-track="{ category: 'Standard', action: standard.isExpanded ? 'open' : 'close', label: standard.title }"
                    >
                        <td>
                            <div v-if="expandable">
                                <v-fab-transition hide-on-leave>
                                    <v-btn icon aria-label="Show standard" v-if="!standard.isExpanded">
                                        <v-icon color="primary">mdi-plus</v-icon>
                                    </v-btn>
                                </v-fab-transition>
                                <v-fab-transition hide-on-leave>
                                    <v-btn icon aria-label="Hide standard" v-if="standard.isExpanded">
                                        <v-icon color="primary">mdi-minus</v-icon>
                                    </v-btn>
                                </v-fab-transition>
                            </div>
                        </td>
                        <td v-if="!areaOfNeedColumns">{{ standard.sdo || 'N/A' }}</td>
                        <td v-if="!areaOfNeedColumns" class="text-center">
                            <v-icon v-if="standard.hasBundles || standard.fda" color="primary">mdi-check</v-icon>
                        </td>
                        <td v-if="!areaOfNeedColumns">{{ standard.standardId || 'N/A' }}</td>
                        <td>{{ standard.title }}</td>
                        <td v-if="!areaOfNeedColumns">
                            <div class="text-no-wrap">{{ standard.statusText }}</div>
                            <div class="text-no-wrap">{{ standard.statusDate }}</div>
                        </td>
                        <td v-if="!areaOfNeedColumns">{{ standard.type || 'N/A' }}</td>
                        <td v-if="areaOfNeedColumns">{{ standard.priority || 'N/A' }}</td>
                        <td v-if="showFeaturedIcons">
                            <v-icon x-large v-if="standard.activeFeatureIcon" color="primary">${{ standard.activeFeatureIcon }}</v-icon>
                        </td>
                    </tr>
                    <tr :key="index" class="details" v-if="standard.isExpanded">
                        <td :colspan="headers.length" class="px-5">
                            <v-container fluid>
                                <v-row>
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Applicable Sector(s)</v-col>
                                    <v-col>
                                        <v-chip v-for="(entry, index) in standard.sectors" :key="index" label class="mr-1 mb-1 grey lighten-4">
                                            <v-icon class="ml-n1 mr-1" color="primary">{{ icon(entry) }}</v-icon>
                                            {{ entry }}
                                        </v-chip>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Functional Area(s)</v-col>
                                    <v-col>
                                        <v-chip v-for="(entry, index) in standard.functionalAreas" :key="index" label class="mr-1 mb-1 grey lighten-4">
                                            <v-icon class="ml-n1 mr-1" color="primary">{{ icon(entry) }}</v-icon>
                                            {{ entry }}
                                        </v-chip>
                                    </v-col>
                                </v-row>
                                <v-row v-if="'Area of Need' === standard.status">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Current Priority</v-col>
                                    <v-col>
                                        <div><a @click.prevent="selectPriority(standard.priority)">{{ standard.priority }}</a></div>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Description</v-col>
                                    <v-col>
                                        <Markdown :text="standard.description"></Markdown>
                                    </v-col>
                                </v-row>
                                <v-row v-if="standard.keywords">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Additional Keywords</v-col>
                                    <v-col>
                                        <div>{{ standard.keywords }}</div>
                                    </v-col>
                                </v-row>
                                <v-row v-if="'Area of Need' === standard.status">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Challenge</v-col>
                                    <v-col>
                                        <Markdown :text="standard.challenge"></Markdown>
                                    </v-col>
                                </v-row>
                                <v-row v-if="'Area of Need' === standard.status">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Standard Solution</v-col>
                                    <v-col>
                                        <Markdown :text="standard.objective"></Markdown>
                                    </v-col>
                                </v-row>
                                <v-row v-if="'Area of Need' === standard.status">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Potential Areas to Standardize</v-col>
                                    <v-col>
                                        <Markdown :text="standard.areasToStandardize"></Markdown>
                                    </v-col>
                                </v-row>
                                <v-row v-if="'Area of Need' === standard.status">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Related Efforts</v-col>
                                    <v-col>
                                        <ul>
                                            <li v-for="(relatedEffort, index) in standard.relatedEfforts" :key="index">
                                                <a :href="relatedEffort.link" target="_blank" v-track v-if="relatedEffort.link">{{ relatedEffort.text || relatedEffort.link }}</a>
                                                <span v-else>{{ relatedEffort.text }}</span>
                                                <v-icon small right color="primary" v-if="relatedEffort.scb">$Scb</v-icon>
                                            </li>
                                        </ul>
                                    </v-col>
                                </v-row>
                                <v-row v-if="'Area of Need' === standard.status">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Community Call to Action</v-col>
                                    <v-col>
                                        <Markdown :text="standard.callToAction"></Markdown>
                                    </v-col>
                                </v-row>
                                <v-row v-if="'Area of Need' !== standard.status && standard.callToAction">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Updates and Calls to Action</v-col>
                                    <v-col>
                                        <Markdown :text="standard.callToAction"></Markdown>
                                    </v-col>
                                </v-row>
                                <v-row v-if="'Area of Need' !== standard.status">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Availability</v-col>
                                    <v-col>
                                        <div v-html="standard.availability || ''" v-linkify></div>
                                    </v-col>
                                </v-row>
                                <v-row v-if="'Area of Need' !== standard.status && standard.fda">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">FDA Recognized</v-col>
                                    <v-col>
                                        <span>This standard has been recognized by the <a href="https://www.fda.gov/vaccines-blood-biologics/standards-development-regenerative-medicine-therapies" target="_blank" v-track>FDA CBER Voluntary Consensus Standards Recognition Program for Regenerative Medicine Therapies (SRP-RMT)</a>.</span>
                                    </v-col>
                                </v-row>
                                <v-row v-if="'Area of Need' !== standard.status && standard.hasBundles">
                                    <v-col cols="2" class="font-weight-bold text-right orange--text">Curated ANSI Package(s)</v-col>
                                    <v-col>
                                        <Markdown :text="standard.bundles"></Markdown>
                                    </v-col>
                                </v-row>
                                <v-row class="align-center">
                                    <v-col cols="auto" v-if="'Area of Need' === standard.status">
                                        <v-icon small left color="primary">$Scb</v-icon>
                                        = SCB-coordinated project
                                    </v-col>
                                    <v-col cols="auto" v-if="'Area of Need' === standard.status">
                                        <mark>Highlight</mark>
                                        = Recent activity
                                    </v-col>
                                    <v-spacer></v-spacer>
                                    <v-col cols="auto" class="text-right">
                                        <a @click="feedback(standard)">
                                            <v-icon color="primary">mdi-comment-alert</v-icon>
                                            Feedback or updates on this item
                                        </a>
                                        <ClipboardCopy :text="shareLink(standard)" tooltip="Get a unique share link for this item" copiedTooltip="Link copied to clipboard!">
                                            <template #default="{ on, attrs, click }">
                                                <a @click="click()" v-bind="attrs" v-on="on" class="ml-6">
                                                    <v-icon color="primary">mdi-link</v-icon>
                                                    Share link
                                                </a>
                                            </template>
                                        </ClipboardCopy>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </td>
                    </tr>
                </template>
            </tbody>
        </template>
    </v-simple-table>
</template>

<script>
    import moment from 'moment';
    import { camelCase, find, reverse, sortBy, toUpper } from 'lodash';
    import ClipboardCopy from '@/components/ClipboardCopy';
    import Markdown from '@/components/Markdown';
    import SortableTableHeader from '@/components/SortableTableHeader';
    import { icon } from '@/common';
    import router from '@/router';
    import goTo from 'vuetify/es5/services/goto';

    export default {
        components: { ClipboardCopy, Markdown, SortableTableHeader },
        data: () => ({
            headers: [
                { text: '', sortable: false, sort: null },
                { text: 'Organization', value: 'sdo', sortable: true, sort: null },
                { text: 'Designations', value: 'designations', sortable: false, sort: null, align: 'center' },
                { text: 'ID', value: 'standardId', sortable: true, sort: null },
                { text: 'Title', value: 'title', sortable: true, sort: null },
                { text: 'Status', value: 'status', sortable: true, sort: null },
                { text: 'Type', value: 'type', sortable: true, sort: null },
                { text: '', sortable: false, sort: null }
            ]
        }),
        props: {
            standards: {
                type: Array,
                required: true
            },
            expandable: {
                type: Boolean,
                default: true
            },
            sortable: {
                type: Boolean,
                default: true
            },
            showFeaturedIcons: {
                type: Boolean,
                default: true
            },
            areaOfNeedColumns: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            sortColumn() {
                return find(this.headers, { sortable: true, sort: 'asc' }) || find(this.headers, { sortable: true, sort: 'desc' });
            },
            sortedStandards() {
                if (this.sortColumn) {
                    const sorted = (() => {
                        if ('status' === this.sortColumn.value) {
                            return sortBy(this.standards, (standard) => {
                                const year = standard[`${camelCase(standard.status)}Year`];
                                const month = standard[`${camelCase(standard.status)}Month`];
                                let date = null;
                                if (year) {
                                    if (month) {
                                        date = moment(`${year} ${month}`, 'YYYY MMMM');
                                    } else {
                                        date = moment(`${year}`, 'YYYY');
                                    }
                                }
                                const secondary = Number.MAX_SAFE_INTEGER - (date ? date.unix() : 0);
                                return `${standard.statusText} ${secondary}`;
                            });
                        }
                        return sortBy(this.standards, (standard) => {
                            return toUpper(standard[this.sortColumn.value] || '');
                        });
                    })();
                    if ('desc' === this.sortColumn.sort) {
                        return reverse(sorted);
                    }
                    return sorted;
                }
                return this.standards;
            }
        },
        methods: {
            icon: icon,
            shareLink(standard) {
                return `${window.location.origin}/standard/${standard.hex}`;
            },
            feedback(standard) {
                this.$root.$contactDialog.show(standard);
            },
            selectPriority(priority) {
                this.$store.set('selectedPriority', priority);
                if ('/' !== router.currentRoute.path) {
                    router.push('/#priorities');
                } else {
                    goTo('#priorities');
                }
            }
        },
        mounted() {
            if (this.areaOfNeedColumns) {
                this.headers = [
                    { text: '', sortable: false, sort: null },
                    { text: 'Title', value: 'title', sortable: true, sort: null },
                    { text: 'Priority', value: 'priority', sortable: true, sort: null },
                    { text: '', sortable: false, sort: null }
                ];
            }
            if (!this.sortable) {
                this.headers.forEach((header) => {
                    header.sortable = false;
                });
            }
            if (!this.showFeaturedIcons) {
                this.headers.splice(-1, 1);
            }
        }
    };
</script>

<style lang="scss" scoped>
    $table-border: thin solid #808080;
    $table-spacing: 10px;

    .results tbody tr.summary {
        cursor: pointer;
    }
    .results tbody tr.summary.area-of-need td {
        font-style: italic;
        background-color: rgba(128, 128, 128, 0.12);
    }
    .results tbody tr.summary.expanded td {
        border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
    }
    .results tbody tr.details:hover {
        background: none !important;
    }
    .results tbody tr.details td {
        position: relative;
        top: -#{$table-spacing};
        border-top: none !important;
        white-space: pre-wrap;
    }
    .results tbody tr.details ::v-deep .v-chip:hover::before {
        opacity: 0;
    }
    .results ::v-deep table {
        border-spacing: 0 $table-spacing !important;
        position: relative;
        top: -#{$table-spacing * 2};
    }
    .results thead ::v-deep th {
        font-family: $heading-font-family;
        text-transform: uppercase;
        white-space: nowrap !important;
        opacity: 0.75;
        border: none !important;
    }
    .results thead ::v-deep th * {
        position: relative;
        top: #{$table-spacing * 2};
    }
    .results tbody td {
        border-top: $table-border !important;
        border-bottom: $table-border !important;
        padding: 8px 16px;
    }
    .results tbody td:first-child {
        border-left: $table-border !important;
    }
    .results tbody td:last-child {
        border-right: $table-border !important;
    }
</style>
